'use client';

import {
  PlusIcon,
  TrashIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { Button, HeroIcon } from '@/components/atoms';
import { List, LikeButton } from '@/components/molecules';
import { useAuthContext } from '@/lib/contexts/auth';
import { useDialog } from '@/lib/hooks/auth/useDialog';
import { useBreakpoint } from '@/lib/hooks/util/useBreakpoint';
import { cn } from '@/lib/utils/tailwind';

import { AddMenu } from './AddMenu';
import { ApprovalModal } from './ApprovalModal';
import { TrackOptionsMenu } from './TrackOptionsMenu';

export const ItemSuffix = ({
  id,
  isPersonalPlaylistTrack,
  handleDelete,
  playlistId,
  onTrackRemoved,
}: {
  id: number;
  handleLike?: () => void;
  handleAdd?: () => void;
  handleMore?: () => void;
  handleDelete?: () => void;
  isPersonalPlaylistTrack?: boolean;
  playlistId?: number;
  onTrackRemoved?: () => void;
}) => {
  const isDesktop = useBreakpoint('md');
  const t = useTranslations();
  const { isAuthenticated } = useAuthContext();
  const { openDialog } = useDialog();
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [optionsMenuOpen, setOptionsMenuOpen] = useState(false);
  const confirmRemoveTrack = async () => {
    if (playlistId && handleDelete) {
      handleDelete();
    }
    setDeleteModalOpen(false);
  };

  const handleAddButtonClick = () => {
    if (!isAuthenticated) {
      openDialog('auth');
      return;
    }
    setAddMenuOpen(true);
  };
  return (
    <>
      <List.ItemSuffix className={cn('flex items-center p-0 m-0')}>
        {isDesktop ? (
          <>
            <LikeButton contentId={id} contentType="Track" iconOnly />
            <Button
              variant={'ghost'}
              onClick={handleAddButtonClick}
              aria-label={t('SAVE_IN_OTHER_PLAYLIST')}
              aria-haspopup="dialog"
              aria-expanded={addMenuOpen}
            >
              <HeroIcon icon={PlusIcon} size="medium" />
            </Button>
            {isPersonalPlaylistTrack && (
              <Button
                variant={'ghost'}
                onClick={() => setDeleteModalOpen(true)}
                aria-label={t('MENUMORE_REMOVE_FROM_PLAYLIST')}
                aria-haspopup="dialog"
                aria-expanded={deleteModalOpen}
              >
                <HeroIcon icon={TrashIcon} size="medium" />
              </Button>
            )}
          </>
        ) : (
          <Button
            variant={'ghost'}
            onClick={() => setOptionsMenuOpen(true)}
            aria-label={t('MORE_OPTIONS')}
            aria-haspopup="dialog"
            aria-expanded={optionsMenuOpen}
          >
            <HeroIcon icon={EllipsisHorizontalIcon} size="medium" />
          </Button>
        )}
      </List.ItemSuffix>
      {isAuthenticated && (
        <>
          <AddMenu
            drawerOpen={addMenuOpen}
            drawerOpenAction={setAddMenuOpen}
            trackId={id}
          />
          {deleteModalOpen && (
            <ApprovalModal
              modalOpen={deleteModalOpen}
              modalOpenAction={setDeleteModalOpen}
              modalTitle={t('EXPANDED_PLAYER_SETTINGS')}
              modalSubtitle={t('MY_PLAYLISTS_DELETE_TRACK_POPUP')}
              modalCTA={t('MY_PLAYLIST_DELETE_TRACK_CTA')}
              onConfirm={confirmRemoveTrack}
            />
          )}
        </>
      )}
      <TrackOptionsMenu
        drawerOpen={optionsMenuOpen}
        drawerOpenAction={setOptionsMenuOpen}
        trackId={id}
        playlistId={playlistId}
        isPersonalPlaylistTrack={isPersonalPlaylistTrack}
        onTrackRemoved={onTrackRemoved}
      />
    </>
  );
};
